import { constants as C } from '@trucktrax/trucktrax-common';
import { DriverDto } from '@trucktrax/trucktrax-ts-common';
import { isEqual } from 'lodash';

import { DTO_URL_PREFIXES } from '../../../../constants/appConstants';
import { getFullName } from '../../../../util/adminUtil';
import { isEmptyString } from '../../../../util/appUtil';
import recordLog, { LogLevel } from '../../../../util/errorUtil';
import { PositionMarkerDataInfo } from '../../../../util/markerUtil';
import { MarkerType } from '../TTMap';
// Type-only import: erased at compile time, so no runtime cycle with the
// component module that imports this file's values.
import type { PositionMarkerProps } from './PositionMarker';

export type PositionNameSource = Partial<Pick<PositionMarkerDataInfo, 'trackedObject' | 'status'>>;
export type MarkerTypeInfo = MarkerType & { trackedObjectName?: string };
export type MarkerTypeNameSource = Pick<
  MarkerTypeInfo,
  'driverDetails' | 'truckDetails' | 'trackedObjectName'
>;

type EtaMeasurement = { value?: number; text?: string };

/** markerData.positionDetails: the live position plus the ETA inputs. */
export type MarkerPositionDetails = PositionMarkerDataInfo & {
  distance?: EtaMeasurement;
  duration?: EtaMeasurement;
  plantLocation?: unknown;
};

// String(e) rather than interpolation: template literals throw on Symbols,
// which would defeat the error-containment paths that call this.
export const describeError = (e: unknown): string => (e instanceof Error ? e.message : String(e));

export const extractDriverName = (markerData: MarkerTypeNameSource) => {
  let driverName = getFullName(markerData?.driverDetails);
  driverName ??= getFullName(markerData?.truckDetails?.driver);
  if (isEmptyString(driverName)) {
    recordLog(LogLevel.Warn, 'Could not extract driver name from marker data!', { markerData });
  }
  return driverName;
};

export const determineTrackedObjectName = (
  position: PositionNameSource,
  markerData: MarkerTypeNameSource,
): string | undefined => {
  if (markerData.trackedObjectName) {
    return markerData.trackedObjectName;
  }

  if (position.trackedObject?.truckAlias) {
    return position.trackedObject.truckAlias;
  }
  if (position.trackedObject?.url?.includes(DTO_URL_PREFIXES.DRIVERS)) {
    const driver = position.trackedObject as DriverDto;
    return extractDriverName({
      driverDetails: { firstName: driver.firstName ?? '', lastName: driver.lastName ?? '' },
    });
  }
  if (markerData.truckDetails?.truckAlias) {
    return markerData.truckDetails.truckAlias;
  }
  const name = extractDriverName(markerData);
  return name;
};

export const correctStatusByProductLine = (position: PositionMarkerDataInfo): string => {
  let { status } = position;
  if (!status) {
    recordLog(LogLevel.Warn, 'Position is missing a status setting it to unknown', {
      positionId: position?.id,
      driver: position.driver?.url,
      trackedObject: position.trackedObject,
    });
    return C.STATUS_IDS.UNKNOWN;
  }

  if (position.productLine !== C.READY_MIX_VALUE && status === C.STATUS_IDS.LOADING) {
    status = C.STATUS_IDS.TICKETED;
  }
  return status;
};

export const generateStatusInfo = (
  position: PositionMarkerDataInfo,
  markerStatusMap: Record<string, string>,
): { statusText: string; statusColor: string } => {
  const { status } = position;
  return {
    statusText: markerStatusMap[status!] || markerStatusMap[C.STATUS_IDS.UNKNOWN],
    statusColor: (C.STATUS_COLORS as any)[status!] || C.STATUS_COLORS.Unknown,
  };
};

export type RenderInputs =
  | {
      ok: true;
      name: string;
      renderPosition: PositionMarkerDataInfo;
      statusColor: string;
      statusText: string;
    }
  | { ok: false; error?: unknown };

/**
 * Derives everything PositionMarker's render needs from the raw props in one
 * contained pass: the display name, the status-corrected position, and the
 * status color/text.
 *
 * The corrected status lives on a local copy (`renderPosition`) instead of
 * being written back onto the prop. Mutating the prop (which is the Redux
 * store's own position object) made the memo comparator see the corrected
 * status on the previous props but the raw status on the next frame — e.g.
 * Ticketed vs Loading for non-ReadyMix trucks — forcing a re-render on every
 * frame for exactly those trucks.
 */
export const deriveRenderInputs = (
  position: PositionMarkerDataInfo | undefined,
  markerData: MarkerTypeInfo | undefined,
  markerStatusMap: Record<string, string>,
): RenderInputs => {
  if (!position || !markerData) {
    return { ok: false };
  }
  try {
    const name = determineTrackedObjectName(position as PositionNameSource, markerData);
    if (isEmptyString(name)) {
      return { ok: false };
    }
    const renderPosition = { ...position, status: correctStatusByProductLine(position) };
    const { statusColor, statusText } = generateStatusInfo(renderPosition, markerStatusMap);
    return { ok: true, name: name!, renderPosition, statusColor, statusText };
  } catch (error) {
    return { ok: false, error };
  }
};

/**
 * Compares the `position` fields PositionMarker (and its helpers) actually
 * read during render. Position objects arrive as fresh references on every
 * SignalR frame (~25/s fleet-wide, DEV-4500), so a full deep-equal per marker
 * per frame was a measurable CPU cost. Anything newly read from `position` in
 * render must be added here, or markers will render stale for that field.
 *
 * Deliberately absent: the raw frame `timestamp` — it changes on every frame
 * but nothing reads it during render (the info pane's Date/Time comes from
 * truckDetails.timestamp or updatedTimestamp). Adding it would force a
 * re-render per frame for parked trucks and defeat this comparator.
 */
export const positionRenderFieldsEqual = (
  prev: PositionMarkerDataInfo | undefined,
  next: PositionMarkerDataInfo | undefined,
): boolean => {
  if (prev === next) {
    return true;
  }
  if (!prev || !next) {
    return false;
  }
  const prevTrackedDriver = prev.trackedObject as DriverDto | undefined;
  const nextTrackedDriver = next.trackedObject as DriverDto | undefined;
  return (
    prev.id === next.id &&
    // presence matters on its own: the View Ticket button gates on
    // !!position.trackedObject even when no compared sub-field is set
    !!prev.trackedObject === !!next.trackedObject &&
    prev.status === next.status &&
    prev.bearing === next.bearing &&
    prev.speed === next.speed &&
    prev.active === next.active &&
    prev.historical === next.historical &&
    prev.productLine === next.productLine &&
    prev.updatedTimestamp === next.updatedTimestamp &&
    prev.statusTimestamp === next.statusTimestamp &&
    prev.location?.latitude === next.location?.latitude &&
    prev.location?.longitude === next.location?.longitude &&
    (prev.location as any)?.lat === (next.location as any)?.lat &&
    (prev.location as any)?.lng === (next.location as any)?.lng &&
    prev.trackedObject?.url === next.trackedObject?.url &&
    prev.trackedObject?.truckAlias === next.trackedObject?.truckAlias &&
    prev.trackedObject?.isExternal === next.trackedObject?.isExternal &&
    prevTrackedDriver?.firstName === nextTrackedDriver?.firstName &&
    prevTrackedDriver?.lastName === nextTrackedDriver?.lastName &&
    prev.driver?.url === next.driver?.url &&
    (prev.driver as DriverDto)?.isExternal === (next.driver as DriverDto)?.isExternal &&
    prev.ticket?.url === next.ticket?.url &&
    prev.region?.url === next.region?.url
  );
};

/**
 * markerData embeds the live position as `positionDetails` (TTMap builds it as
 * {...marker.data, trackedObjectName}), so a plain deep-equal of markerData
 * would walk the entire position per marker per frame — and fail on the raw
 * frame timestamp every time. Compare positionDetails by the render-relevant
 * fields plus the ETA inputs generateEtaDetailsContent reads, and everything
 * else in markerData (ticket, truckDetails, driverDetails, …) deeply.
 */
const markerPositionDetailsEqual = (
  prev: MarkerPositionDetails | undefined,
  next: MarkerPositionDetails | undefined,
): boolean =>
  positionRenderFieldsEqual(prev, next) &&
  prev?.distance?.value === next?.distance?.value &&
  prev?.distance?.text === next?.distance?.text &&
  prev?.duration?.value === next?.duration?.value &&
  prev?.duration?.text === next?.duration?.text &&
  !!prev?.plantLocation === !!next?.plantLocation &&
  (prev?.ticket == null) === (next?.ticket == null);

export const markerDataEqual = (
  prev: MarkerTypeInfo | undefined,
  next: MarkerTypeInfo | undefined,
): boolean => {
  if (prev === next) {
    return true;
  }
  if (!prev || !next) {
    return false;
  }
  const { positionDetails: prevPosition, ...prevRest } = prev;
  const { positionDetails: nextPosition, ...nextRest } = next;
  return markerPositionDetailsEqual(prevPosition, nextPosition) && isEqual(prevRest, nextRest);
};

/**
 * Replaces the previous whole-props lodash isEqual (DEV-4500): scalar and
 * callback props compare by identity, `position` by the render-relevant
 * fields above, `markerData` by markerDataEqual, and `markerStatusMap`
 * (small bounded map) deeply.
 */
export const propsAreEqual = (prevProps: PositionMarkerProps, nextProps: PositionMarkerProps) => {
  const prevKeys = Object.keys(prevProps) as Array<keyof PositionMarkerProps>;
  if (prevKeys.length !== Object.keys(nextProps).length) {
    return false;
  }

  return prevKeys.every(key => {
    switch (key) {
      case 'position':
        return positionRenderFieldsEqual(prevProps.position, nextProps.position);
      case 'markerData':
        return markerDataEqual(prevProps.markerData, nextProps.markerData);
      case 'markerStatusMap':
        return isEqual(prevProps[key], nextProps[key]);
      default:
        return prevProps[key] === nextProps[key];
    }
  });
};
